/**
 * Configuración del MediaKit
 * 
 * Este archivo centraliza toda la configuración para la página /mediakit-intro
 * Edita aquí para actualizar tu media kit sin tocar el código del componente
 */

export interface MediaKitPage {
  id: number;
  image: string; // public_id de Cloudinary o URL completa (desktop)
  imageMobile?: string; // public_id para versión móvil (opcional)
  alt: string;
  title?: string; // Título opcional para cada página
}

export const MEDIAKIT_CONFIG = {
  // Páginas del media kit
  // Puedes usar public_ids de Cloudinary (ej: "mediakit/page-1")
  // O URLs completas (ej: "https://...")
  pages: [
    {
      id: 1,
      image: "mediakit/page_one",
      imageMobile: "mediakit/page_one_mobile",
      alt: "MediaKit - Página 1",
      title: "Página 1",
    },
    {
      id: 2,
      image: "mediakit/page_two",
      imageMobile: "mediakit/page_two_mobile",
      alt: "MediaKit - Página 2",
      title: "Página 2",
    },
    {
      id: 3,
      image: "mediakit/page_three",
      imageMobile: "mediakit/page_three_mobile",
      alt: "MediaKit - Página 3",
      title: "Página 3",
    },
  ] as MediaKitPage[],

  // URL del PDF completo para descarga
  // Déjalo vacío ("") si no quieres ofrecer descarga
  pdfDownloadUrl: "https://your-cloudflare-url.com/mediakit.pdf",

  // Textos configurables (deprecated — use i18n keys from mediaKit.* namespace)
  // Kept for backward compatibility with any external references
  texts: {
    pageTitle: "Media Kit",
    pageSubtitle: "Presentación Digital",
    pageDescription: "Explora mi media kit interactivo. Usa las flechas o desliza para navegar entre páginas.",
    downloadButtonText: "Descargar PDF",
    fullscreenButtonText: "Ver en pantalla completa",
    backLinkText: "Volver al inicio",
    loadingText: "Cargando...",
    swipeHint: "Desliza o usa las flechas para navegar",
  },

  // Configuración de imágenes
  imageSettings: {
    // Calidad de las imágenes (1-100)
    quality: 90,
    
    // Anchos para responsive images
    widths: {
      thumbnail: 200,
      small: 600,
      medium: 900,
      large: 1200,
      xlarge: 1800,
    },

    // Agregar watermark de Cloudinary (si está configurado)
    useWatermark: false,

    // Formato automático de Cloudinary (webp, avif, auto)
    format: "auto" as const,
  },

  // Aspect ratio de las páginas
  // Ejemplos: "210 / 297" (A4), "8.5 / 11" (Carta US), "16 / 9", "1 / 1" (cuadrado)
  aspectRatio: "210 / 297",

  // Habilitar/deshabilitar funcionalidades
  features: {
    enableDownload: true,
    enableFullscreen: true,
    enableThumbnails: true,
    enableKeyboardNav: true,
    enableSwipeGestures: true,
  },

  // Colores (Tailwind classes)
  colors: {
    primary: "black",
    primaryHover: "black/90",
    secondary: "white",
    accent: "black/60",
    background: "gray-50",
    backgroundGradient: "from-gray-50 to-gray-100",
  },

  // Animaciones
  animations: {
    // Velocidad de transición entre páginas
    pageTransition: {
      type: "spring" as const,
      stiffness: 300,
      damping: 30,
    },
    
    // Threshold para swipe (en píxeles)
    swipeThreshold: 50,
  },
} as const;

/**
 * Helper para obtener la configuración de páginas
 */
export const getMediaKitPages = () => MEDIAKIT_CONFIG.pages;

/**
 * Helper para verificar si una URL es de Cloudinary
 */
export const isCloudinaryUrl = (url: string): boolean => {
  return !url.startsWith("http");
};

/**
 * Helper para obtener la URL del PDF
 */
export const getPdfDownloadUrl = (): string | null => {
  return MEDIAKIT_CONFIG.pdfDownloadUrl || null;
};
